/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';

import { CheckboxInput, Typography2 } from '@coursera/cds-core';

import _t from 'i18n!nls/enterprise-ui';

const styles = {
  bar: css`
    background-color: var(--cds-color-emphasis-primary-background-strong);
    padding: var(--cds-spacing-150) var(--cds-spacing-300);
    border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0;
    width: 100%;
    min-height: 60px;
    display: flex;
    gap: var(--cds-spacing-300);
    align-items: center;
    position: sticky;
    top: 66px;
    z-index: 4;
  `,
  actions: css`
    display: flex;
    gap: var(--cds-spacing-50);
    align-items: center;
    margin-left: auto;
  `,
};

type Props = {
  /** Text describing the current selection (e.g. "3 groups selected" or "All groups selected"). */
  label: string;
  /** When true, the checkbox renders as fully checked; otherwise indeterminate (partial selection). */
  selectAll: boolean;
  /** Called when the user clicks the checkbox to clear the selection. */
  onUnselect: () => void;
  /** Action buttons to render on the right side of the bar (e.g. "Send message", "Assign content"). */
  children?: ReactNode;
};

/**
 * A sticky action bar displayed above the table header when one or more rows are selected.
 *
 * Renders a checked/indeterminate checkbox (reflecting whether all or some rows are selected),
 * a selection count label, and a slot for contextual action buttons on the right.
 * Intended to be composed inside `ActionTable`'s `bulkActions` prop.
 *
 * @example
 * ```tsx
 * <ActionTable
 *   ...
 *   bulkActions={
 *     <BulkActionBar
 *       label={selectAll ? 'All groups selected' : `${selectedKeys.size} groups selected`}
 *       selectAll={selectAll}
 *       onUnselect={() => { setSelectedKeys(new Set()); setSelectAll(false); }}
 *     >
 *       <Button variant="ghostInvert" size="small">Send message</Button>
 *     </BulkActionBar>
 *   }
 * />
 * ```
 */
export const BulkActionBar = ({ label, selectAll, onUnselect, children }: Props) => {
  return (
    <div css={styles.bar}>
      <CheckboxInput
        checked
        indeterminate={!selectAll}
        aria-label={selectAll ? _t('Deselect all rows') : _t('Deselect selected rows')}
        onChange={onUnselect}
      />
      <Typography2 component="span" variant="actionPrimary" color="invertBody">
        {label}
      </Typography2>
      {children && <div css={styles.actions}>{children}</div>}
    </div>
  );
};
